import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Award, Calendar, BookOpen, ExternalLink, Download } from "lucide-react";

const MyCertificates = () => {
  const { user } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/certificates`, { withCredentials: true });
      setCertificates(response.data);
    } catch (error) {
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="my-certificates">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
            My Certificates
          </h1>
          <p className="text-stone-600 mt-1">Your achievements and completed courses</p>
        </div>

        {/* Certificates Grid */}
        {certificates.length === 0 ? (
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-12 text-center">
              <Award className="h-16 w-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-stone-900 mb-2">No certificates yet</h3>
              <p className="text-stone-500 mb-4">Complete courses to earn certificates</p>
              <Link to="/courses">
                <Button className="bg-violet-700 hover:bg-violet-800 rounded-full">
                  Browse Courses
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {certificates.map((cert) => (
              <Card 
                key={cert.certificate_id}
                className="bg-white rounded-xl border border-stone-100 overflow-hidden hover:shadow-lg transition-all"
                data-testid={`certificate-card-${cert.certificate_id}`}
              >
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-violet-600 to-violet-800 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <Award className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-white">
                      <p className="text-xs uppercase tracking-wide text-violet-200">Certificate</p>
                      <p className="font-mono text-sm">{cert.certificate_number}</p>
                    </div>
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 
                    className="text-xl font-semibold text-stone-900 mb-2"
                    style={{ fontFamily: 'Outfit' }}
                  >
                    {cert.course_title}
                  </h3>

                  <div className="flex items-center gap-4 text-sm text-stone-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(cert.completed_at)}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Link to={`/certificate/${cert.certificate_id}`} className="flex-1">
                      <Button 
                        className="w-full bg-violet-700 hover:bg-violet-800 rounded-xl"
                        data-testid={`view-certificate-${cert.certificate_id}`}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Certificate
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyCertificates;
